import React, { useEffect, useState } from 'react';
import { Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { Icon as Iconify } from '@iconify/react';
import { useLocation} from 'react-router-dom';
import { isEqual } from 'lodash';

import { useSchemeStore } from '../schemeStore';
import { renderControls } from '../utils/renderControls';
import ConfirmationPopup from '../Others/ConfirmationPopup2';
import PopUpHandle from '../../../commons/PopUpHandle';

export default function SchemeDefinition() {
    const {
        schemeDetails,
        schemeControls,
        primaryCategoryControls,
        executiveDefControls,
        schemeDivisionControls,
        criteriaHandlingControls,
        newCustomerControls,
        distributorBeneficiaryControls,
        parentSchemeDetails,
        fetchParentSchemeDetails,
        setSchemeData,
        tabValue,
        getSchemeDocuments,
        schemeId,
        schemeDocuments,
        updateScheme,
        prevTabValue,
        setDefinitionData,
        validationErrorMessage,
        setLiftingUOMName,
        addScheme,
        conversionRateControl,
        fetchConversionRateControl,
        pointBasedSlabRepeatationCriteriaControls,
        areaData,
        retailerData,
        distributorData,
        itemData,
        schemeData
    } = useSchemeStore();


   
    const location = useLocation();
    const isCopyScheme = location.state?.data?.copyScheme;
    const copySchemeName = location?.state?.data?.copySchemeName?.label


    

    const { handleSubmit, control, watch, setValue, reset, unregister, formState: { errors }, trigger } = useForm({
        defaultValues: {
            ...Object.fromEntries(
                Object.entries(schemeDetails?.SchemeData || {}).filter(
                    ([_, value]) => value != null && value !== undefined
                )
            ),
            To_ToDate: schemeDetails?.To_ToDate,
            From_FromDate: schemeDetails?.From_FromDate,
            Name: isCopyScheme ? `${copySchemeName}-Copy` : schemeDetails?.SchemeData.Name,
        },

        shouldUnregister: true,
        shouldFocusError: true
    });


    /*  ---------------------------- PopUp Message -------------------------------------------------------   */

    const [open, setOpen] = useState(false);
    const [errorr, setErrorr] = useState(0);
    const [errorCode, setErrorCode] = useState(null);


    const handleClose = () => {
        setOpen(false);
    };

    /* --------------------------------------------------------------------------------------  */


    const [confirmationPopupOpen, setConfirmationPopupOpen] = useState(false);

    // Called when user confirms the popup
    const onConfirm = async () => {
        fetchConversionRateControl({});
        setConfirmationPopupOpen(false);
    };

    // Called when user cancels or closes the popup
    const onDismiss = (type) => {
        if (type !== 'close') {            
            setValue("IsApplySchemeConversion", false); // Uncheck the checkbox  
        }
        setConfirmationPopupOpen(false);
    };

    // Opens the confirmation dialog
    const openConfirmationPopup = () => {
        setConfirmationPopupOpen(true);
    };


    useEffect(() => {
        if (!conversionRateControl?.length) {
            setValue("IsApplySchemeConversion", false);    
            return;
        } 

        // Find the control by ValueExpr
        const schemeConversionControl = conversionRateControl.find(
            (controll) => controll.ValueExpr === "SchemeConversionRateId"
        );

        if (
            schemeConversionControl &&
            Array.isArray(schemeConversionControl.DefaultValues) &&
            schemeConversionControl.DefaultValues.length === 0
        ) {
            setErrorCode(400);
            setErrorr('As per selected scheme criteria, No matching conversion rate details found.');
            setOpen(true);
            setValue("IsApplySchemeConversion", false); // Uncheck
        } else if (schemeConversionControl) {
            setValue("IsApplySchemeConversion", true);
        }
    }, [conversionRateControl, setValue, unregister]);





    useEffect(() => {

        const { Name, ...restData } = schemeDetails?.SchemeData || {};
        
        reset({
            ...Object.fromEntries(
                Object.entries(restData || {}).filter(
                    ([_, value]) => value != null
                )
            ),
            Name: isCopyScheme ? `${copySchemeName}-Copy` : schemeDetails?.SchemeData.Name,
            To_ToDate: schemeDetails?.To_ToDate,
            From_FromDate: schemeDetails?.From_FromDate,
        });
        if (schemeDetails?.SchemeDocument && schemeDetails?.SchemeDocument?.length > 0) {
            getSchemeDocuments(schemeId, schemeDetails?.SchemeDocument);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schemeDetails]);


    useEffect(() => {
        if (schemeDocuments.length > 0 && schemeDetails.SchemeDocument?.length === schemeDocuments.length) {
            const files = schemeDocuments.map((blob, idx) => {
                const fileName = schemeDetails.SchemeDocument[idx].Name || `file${idx + 1}`;
                // You may want to detect or specify the correct MIME type
                const type = blob.type || "application/octet-stream";
                return new File([blob], fileName, { type });
            });

            setValue('SchemeDocument', files); // set in react-hook-form
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schemeDocuments]);
    const values = watch();

    useEffect(() => {
        if (values?.SchemeCriteria === 0) {
            setValue('SchemeCriteria', null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.SchemeCriteria])

    useEffect(() => {        
        if (!isEqual(values, schemeData)) {
            setSchemeData(values);
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);


    useEffect(() => {
        if (values?.SchemeApplicable?.toString() === '1' && values?.SchemeBeneficiary?.toString() !== '2' ) {
            setValue('SchemeBeneficiary', 2);
        }   
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);
    

    useEffect(() => {
        if (values?.ParentschemeId) {
            setValue('IsParentScheme', false);
        }
        if (values?.IsParentScheme) {
            setValue('ParentschemeId', null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.IsParentScheme, values?.ParentschemeId]);

    useEffect(() => {
        if (values?.ParentschemeId) {
            fetchParentSchemeDetails(values?.ParentschemeId);
        }   
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.ParentschemeId]);

    useEffect(() => {
        if (values?.ParentschemeId && parentSchemeDetails) {
            setValue('ExcludeException', parentSchemeDetails?.SchemeData?.ExcludeException || false);
            setValue('GroupApplicable', parentSchemeDetails?.SchemeData?.GroupApplicable || false);
            setValue('IsStandard', parentSchemeDetails?.SchemeData?.IsStandard || false);
        }  
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentSchemeDetails]);

    useEffect(() => {
        if (conversionRateControl?.length) {
            const conversionField = conversionRateControl.find(
                (f) => f.ValueExpr === "SchemeConversionRateId"
            );

            if (conversionField) {
                trigger(conversionField.ValueExpr);
            }
        }
    }, [conversionRateControl, trigger]);

    // Utility function
    function isNotEmpty(value) {
        return value != null && (!Array.isArray(value) || value.length > 0);
    }

    

    useEffect(() => {

        if (!schemeDetails) return; // Wait for schemeDetails to be loaded

        if (!values?.IsApplySchemeConversion) {
            return;
        }

        if (schemeDetails?.SchemeData?.SchemeConversionRateId) {
            fetchConversionRateControl({});
        } else {
            const hasAnyData =
                isNotEmpty(areaData) ||
                isNotEmpty(itemData) ||
                isNotEmpty(retailerData) ||
                isNotEmpty(distributorData);
            if (hasAnyData) {
                fetchConversionRateControl({});
            } else {
                openConfirmationPopup();

            }

        }

       

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.IsApplySchemeConversion, schemeDetails]);



    useEffect(() => {
        if (prevTabValue.name === 'Definition') {
            formDataSave();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue]);

    const formDataSave = async () => {

        if (!values?.IsApplySchemeConversion) {
            delete values.SchemeConversionRateId;
        }

        let liftingUOMName = 'Target%'; // Default if not found
        const liftingUOMControl = schemeControls?.find(
            (controll) => controll.ValueExpr === 'LiftingUnitOfMeasurement'
        );

        if (liftingUOMControl) {
            const selectedLiftingUOM = liftingUOMControl.DefaultValues.find(
                (item) => item.LiftingUnitOfMeasurement === values.LiftingUnitOfMeasurement
            );
            liftingUOMName = selectedLiftingUOM ? selectedLiftingUOM.LiftingUnitOfMeasurementName : 'Target%';
        }

        await trigger();
     

        if (errors && Object.keys(errors).length > 0) {
            setDefinitionData('error');
            setLiftingUOMName(liftingUOMName)
        } else {
            setDefinitionData(values);
            setLiftingUOMName(liftingUOMName)
        }



     
    }

    const onSubmit = async (data) => {

        if (!data?.IsApplySchemeConversion) {
            delete data.SchemeConversionRateId;
        }

        let liftingUOMName = 'Target%'; // Default if not found
        const liftingUOMControl = schemeControls?.find(
            (controll) => controll.ValueExpr === 'LiftingUnitOfMeasurement'
        );

        if (liftingUOMControl) {
            const selectedLiftingUOM = liftingUOMControl.DefaultValues.find(
                (item) => item.LiftingUnitOfMeasurement === values.LiftingUnitOfMeasurement
            );
            liftingUOMName = selectedLiftingUOM ? selectedLiftingUOM.LiftingUnitOfMeasurementName : 'Target%';
        }
        await trigger();
         

        if (errors && Object.keys(errors).length > 0) {
            setDefinitionData('error');
            setLiftingUOMName(liftingUOMName)
        } else {
            setDefinitionData(data);
            setLiftingUOMName(liftingUOMName)
        }

         

        try {
            if (isCopyScheme) {
                const result = await addScheme(); // NOSONAR
            } else {

                const result = await updateScheme(); // NOSONAR
            }
        } catch (err) {
            // handle error
        }
    };


    


    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%', // Make sure parent takes full height
        }}>
            {open && (
                <PopUpHandle open={open} handleClose={handleClose} error={errorr} errorCode={errorCode} />
            )}
            <FormProvider >
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minHeight: 0, // Enables child to grow correctly inside a flex container
                    }}
                >
                    {/* Scrollable content */}
                    <div style={{
                        flexGrow: 1,
                        overflow: 'auto',
                        minHeight: 0,
                    }}>
                        <Box
                            sx={{
                                mt: 1,
                                flexGrow: 1,
                                rowGap: 2,
                                columnGap: 2,
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                    md: '1fr 1fr 1fr',
                                    lg: '1fr 1fr 1fr',
                                    xl: '1fr 1fr 1fr'
                                },
                                gridAutoRows: 'min-content',
                                paddingTop: '0.4rem',
                            }}
                        >
                            <style>
                                {`.MuiFormControlLabel-labelPlacementEnd { max-height: 20px; }`}
                            </style>

                            {schemeControls?.map((field, index) => {
                                if (field.ValueExpr === 'IsActive') return null;

                                return (
                                    <React.Fragment key={field.ValueExpr || index}>
                                        {renderControls(field, index, control, null, null, values)}

                                        {/* Inline dependent rendering */}
                                        {field.ValueExpr === 'SchemeApplicable' &&
                                            values?.SchemeApplicable?.toString() === '1' &&
                                            values?.SchemeBeneficiary?.toString() === '2' &&
                                            primaryCategoryControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'SchemeBeneficiary' &&
                                            values?.SchemeApplicable?.toString() === '2' &&
                                            values?.SchemeBeneficiary?.toString() === '2' &&
                                            distributorBeneficiaryControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'SchemeBeneficiary' &&
                                            values?.SchemeApplicable?.toString() === '2' &&
                                            values?.SchemeBeneficiary?.toString() === '4' &&
                                            executiveDefControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'NewCustomersOnly' &&
                                            values?.NewCustomersOnly &&
                                            newCustomerControls
                                                ?.filter((f) => {
                                                    // Always show all fields EXCEPT "Billng In N Months"
                                                    if (f.ValueExpr !== "NewCustomerBillngNMonths") return true;
                                                    // Show "Billng In N Months" only when billing type is "MinBillingInNMonths"
                                                    return values?.NewCustomerBilling === "MinBillingInNMonths";
                                                })
                                                ?.map((f, i) => renderControls(f, i, control, null, null, values))}
                                                

                                        {field.ValueExpr === 'IsSchemeDivisionWise' &&
                                            values?.IsSchemeDivisionWise &&
                                            schemeDivisionControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'IsPointBasedScheme' &&
                                            values?.IsPointBasedScheme &&
                                            pointBasedSlabRepeatationCriteriaControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'ParentschemeId' &&
                                            values?.ParentschemeId &&
                                            parentSchemeDetails?.SchemeData?.IsConditionalScheme &&
                                            criteriaHandlingControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'IsApplySchemeConversion' &&
                                            values?.IsApplySchemeConversion &&
                                            conversionRateControl?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}                                       

                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    </div>

                    {/* Divider */}
                    <Divider sx={{ mt: 2 }} />
                    
                    {/* Save button fixed at the bottom */}
                    <Box sx={{ mt: 1 }}>
                        <Stack
                            direction="row"
                            justifyContent="flex-end"
                            spacing={2}
                            alignItems="center"
                            sx={{ padding: '2px' }}
                        >
                            {values?.AdditionalRebate && validationErrorMessage && (
                                <Typography color="red" sx={{ mr: 2, fontSize: '0.775rem' }}>
                                    {validationErrorMessage}
                                </Typography>
                            )}
                            {schemeDetails?.SchemeLock !== true && (
                                <Tooltip title="Save">
                                    <IconButton type="submit" sx={{
                                        bgcolor: '#43b5a2',
                                        color: '#fff',
                                        borderRadius: '4px',
                                        height: '30px',
                                        width: '31px',
                                        padding: '4px',
                                    }}>
                                        <Iconify icon="bx:save" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    </Box>
                    {confirmationPopupOpen && (
                        <ConfirmationPopup
                            title="Confirmation"
                            msg="Scheme has no criteria, do you want to proceed?"
                            onConfirm={onConfirm}
                            onDismiss={onDismiss}
                        />
                    )}
                </form>
            </FormProvider>
        </div>
    );
}
